import { skipToken } from "@reduxjs/toolkit/query/react";
import { useClientScope } from "@/components/pages/Client/settings/ClientScopeContext";
import { useGetClientProfileQuery } from "@/redux/apis/clientProfile/clientProfileApi";
import { useAppSelector } from "@/redux/hooks";
import { selectPassengerContactOverrides } from "@/redux/slices/clientBooking/selectors";
import { SELF_PASSENGER_ID } from "@/redux/slices/clientBooking/types";
import { profileSelector } from "@/redux/slices/profile/selectors";

export interface AccountHolder {
  title: string;
  firstName: string;
  lastName: string;
  email: string;
  mobile: string;
  avatar: string;
  /** Suffix for the account-holder's name on a passenger row: " (You)" in self-service, "" on-behalf (the managed client isn't "you"). */
  selfSuffix: string;
}

// `bookedPhone` is the number a booking falls back to when the account has no mobile.
const useProfileAccountHolder = (): { holder: AccountHolder; loaded: boolean; bookedPhone: string } => {
  const { accountId } = useClientScope();
  const profile = useAppSelector(profileSelector);
  const { data: clientProfile } = useGetClientProfileQuery(accountId ? { accountId } : skipToken);

  if (accountId) {
    // Managed (operator/EA) booking: the account holder is the client, never the logged-in user.
    // Until the scoped profile loads, return blanks so callers hide the row rather than flash the
    // operator/EA and then swap.
    const { personal } = clientProfile ?? { personal: undefined };
    return {
      holder: {
        title: personal?.title ?? "",
        firstName: personal?.first_name ?? "",
        lastName: personal?.last_name ?? "",
        email: personal?.email ?? "",
        mobile: personal?.mobile ?? "",
        avatar: clientProfile?.avatar ?? "",
        selfSuffix: "",
      },
      loaded: !!clientProfile,
      bookedPhone: personal?.mobile || personal?.phone || "",
    };
  }

  return {
    holder: {
      title: profile.title,
      firstName: profile.firstName,
      lastName: profile.lastName,
      email: profile.email,
      mobile: profile.mobile,
      avatar: profile.avatar,
      selfSuffix: " (You)",
    },
    loaded: profile.firstName !== undefined,
    bookedPhone: profile.mobile ?? "",
  };
};

/** The account holder's saved details, without this booking's edits; undefined until they load. */
export const useSavedAccountHolder = (): AccountHolder | undefined => {
  const { holder, loaded, bookedPhone } = useProfileAccountHolder();
  return loaded ? { ...holder, mobile: bookedPhone } : undefined;
};

/**
 * The account holder the booking is FOR — the person seeded as Passenger 1 ("Who is this booking
 * for?") and shown in the Booking Summary. In self-service that's the logged-in user, but when an
 * operator or Executive Assistant books on behalf of a client (ClientScopeProvider sets `accountId`)
 * it must be the MANAGED client, not the logged-in operator/EA.
 *
 * The "account holder" surfaces (PassengerPanel self card, TripDetailsPanel "You" row, PassengerRow
 * summary, useMainPassenger, PricedBookingSummary) funnel through here so the scoped identity is
 * resolved in one place and can't drift. "Booked by" is deliberately NOT here — it records the
 * operating user (the operator/EA on an on-behalf booking), which is the opposite resolution; it has
 * its own single source in `useBookedByName`. Mirrors the scoped client-profile fetch PaymentPanel
 * and the booking-confirmation heading already use, so the request is deduped by RTK.
 */
export const useAccountHolder = (): AccountHolder => {
  const profileHolder = useProfileAccountHolder().holder;
  const bookingDetails = useAppSelector(selectPassengerContactOverrides)[SELF_PASSENGER_ID];
  if (!bookingDetails) return profileHolder;
  return {
    ...profileHolder,
    title: bookingDetails.title ?? "",
    firstName: bookingDetails.firstName,
    lastName: bookingDetails.lastName,
    email: bookingDetails.email,
    mobile: bookingDetails.phone,
  };
};
